import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, AutoComplete, Button, Card, Input, InputNumber, Select, Switch, Table } from 'antd';
import { AgencyFeePolicyData, AgencyTaskRule, NewCreatorTier } from '../types';
import { pctToRate, rateToPct } from '../utils';
import CreatorRulesEditor from './creator-rules-editor';
import styles from '../style.module.scss';

interface Props {
  value: AgencyFeePolicyData;
  groups: string[];
  onChange: (next: AgencyFeePolicyData) => void;
}

type CutoffKey = '28' | '29' | '30' | '31';

const moneyFormatter = (v?: number | string): string => String(v ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const moneyParser = (v?: string): number => Number(String(v || '').replace(/\D/g, ''));
const moneyProps = { min: 0, precision: 0, style: { width: '100%' }, formatter: moneyFormatter, parser: moneyParser };
const intProps = { min: 0, precision: 0, style: { width: '100%' } };
const pctProps = { min: 0, max: 50, step: 0.1, style: { width: '100%' } };

const MONTH_INDEX_OPTIONS = [
  { value: 'DAYS_SINCE_JOIN', label: 'Số ngày từ ngày gia nhập: ≤ 31 → tháng 1, ≤ 62 → tháng 2, ≤ 93 → tháng 3 (giống web cũ, mặc định)' },
  { value: 'CUTOFF', label: 'Mốc ngày: gia nhập sau ngày (số ngày của tháng − 10) thì tháng sau là tháng 1 (như trang chính sách)' },
];

/** Sửa Chính sách Phí Agency: quy đổi, NV A/B/C, NV D, Agency nội bộ, email → Agency, Creator đã rời. */
const PolicyEditor = ({ value, groups, onChange }: Props) => {
  const groupOptions = groups.map((g) => ({ value: g, label: g }));

  const setTask = (i: number, patch: Partial<AgencyTaskRule>) =>
    onChange({ ...value, tasks: value.tasks.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) });
  const setTier = (i: number, patch: Partial<NewCreatorTier>) =>
    onChange({ ...value, newCreator: { ...value.newCreator, tiers: value.newCreator.tiers.map((t, idx) => (idx === i ? { ...t, ...patch } : t)) } });
  const setTierMonth = (i: number, field: 'fees' | 'pct', m: number, v: number) => {
    const t = value.newCreator.tiers[i];
    const next = [...t[field]] as [number, number, number];
    next[m] = v;
    setTier(i, { [field]: next });
  };
  const setCutoff = (key: CutoffKey, v: number) =>
    onChange({ ...value, newCreator: { ...value.newCreator, firstMonthCutoff: { ...value.newCreator.firstMonthCutoff, [key]: v } } });
  const setMap = (i: number, patch: Partial<{ email: string; agency: string }>) =>
    onChange({ ...value, emailAgency: value.emailAgency.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });

  return (
    <>
      <Card size="small" title="Quy đổi thu nhập" className={styles.block}>
        <div className={styles.blockHint}>Phí tính trên thu nhập quy đổi của Creator = KC ÷ (KC / 1 USD) × (đ / 1 USD). Mặc định 200 KC = 1 USD = 25.000 đ.</div>
        <div className={styles.inline}>
          <span>KC / 1 USD</span>
          <InputNumber {...intProps} min={1} style={{ width: 120 }} value={value.income.kcPerUsd} onChange={(v) => onChange({ ...value, income: { ...value.income, kcPerUsd: Number(v) || 1 } })} />
          <span>đ / 1 USD</span>
          <InputNumber {...moneyProps} min={1} style={{ width: 140 }} value={value.income.vndPerUsd} onChange={(v) => onChange({ ...value, income: { ...value.income, vndPerUsd: Number(v) || 1 } })} />
        </div>
      </Card>

      <Card size="small" title="Nhiệm vụ A · B · C" className={styles.block}>
        <div className={styles.blockHint}>Creator đạt đủ ngày, giờ (lấy phần nguyên) và KC trong tháng → Agency được tỷ lệ % trên thu nhập quy đổi của Creator đó.</div>
        <Table
          rowKey="key"
          size="small"
          pagination={false}
          dataSource={value.tasks}
          columns={[
            { title: 'NV', dataIndex: 'key', width: 50 },
            { title: 'Tên', key: 'label', render: (_, t, i) => <Input value={t.label} onChange={(e) => setTask(i, { label: e.target.value })} /> },
            { title: 'Ngày ≥', key: 'days', width: 100, render: (_, t, i) => <InputNumber {...intProps} value={t.days} onChange={(v) => setTask(i, { days: Number(v) || 0 })} /> },
            { title: 'Giờ ≥', key: 'hours', width: 100, render: (_, t, i) => <InputNumber {...intProps} value={t.hours} onChange={(v) => setTask(i, { hours: Number(v) || 0 })} /> },
            { title: 'KC ≥', key: 'kc', width: 130, render: (_, t, i) => <InputNumber {...moneyProps} value={t.kc} onChange={(v) => setTask(i, { kc: Number(v) || 0 })} /> },
            { title: 'Phí (%)', key: 'rate', width: 110, render: (_, t, i) => <InputNumber {...pctProps} value={rateToPct(t.rate)} onChange={(v) => setTask(i, { rate: pctToRate(Number(v)) })} /> },
          ]}
        />
      </Card>

      <Card size="small" title="Nhiệm vụ D · Creator mới" className={styles.block}>
        <div className={styles.blockHint}>
          Creator trong 3 tháng đầu kể từ ngày gia nhập, đạt ngày + giờ và nằm trong dải KC của bậc → Agency được <strong>phí cố định</strong> của tháng đó
          (Phí T1 / T2 / T3). Cột % chỉ là "khoảng phí tham khảo" như trang chính sách, không cộng thêm tiền.
          Xét bậc theo thứ tự từ trên xuống, lấy bậc đầu tiên khớp. KC tối đa để trống = không giới hạn.
        </div>
        <div className={styles.inline} style={{ marginBottom: 8 }}>
          <span>Cách đếm tháng thứ mấy</span>
          <Select
            value={value.newCreator.monthIndex}
            options={MONTH_INDEX_OPTIONS}
            onChange={(v) => onChange({ ...value, newCreator: { ...value.newCreator, monthIndex: v } })}
            style={{ width: 'min(760px, 100%)' }}
          />
        </div>
        {value.newCreator.monthIndex === 'CUTOFF' && (
          <div className={styles.inline} style={{ marginBottom: 8 }}>
            <span>Mốc ngày gia nhập theo số ngày của tháng:</span>
            {(['28', '29', '30', '31'] as CutoffKey[]).map((k) => (
              <span key={k}>
                {k} ngày → sau ngày{' '}
                <InputNumber {...intProps} min={1} max={Number(k)} style={{ width: 70 }} value={value.newCreator.firstMonthCutoff[k]} onChange={(v) => setCutoff(k, Number(v) || 1)} />
              </span>
            ))}
          </div>
        )}
        <Table
          rowKey="_row"
          size="small"
          pagination={false}
          scroll={{ x: 1120 }}
          dataSource={value.newCreator.tiers.map((t, i) => ({ ...t, _row: i }))}
          columns={[
            { title: 'Bậc', key: 'label', width: 90, render: (_, t, i) => <Input value={t.label} onChange={(e) => setTier(i, { label: e.target.value })} /> },
            { title: 'Ngày ≥', key: 'days', width: 80, render: (_, t, i) => <InputNumber {...intProps} value={t.days} onChange={(v) => setTier(i, { days: Number(v) || 0 })} /> },
            { title: 'Giờ ≥', key: 'hours', width: 80, render: (_, t, i) => <InputNumber {...intProps} value={t.hours} onChange={(v) => setTier(i, { hours: Number(v) || 0 })} /> },
            { title: 'KC tối thiểu', key: 'kcMin', width: 120, render: (_, t, i) => <InputNumber {...moneyProps} value={t.kcMin} onChange={(v) => setTier(i, { kcMin: Number(v) || 0 })} /> },
            {
              title: 'KC tối đa', key: 'kcMax', width: 120,
              render: (_, t, i) => <InputNumber {...moneyProps} placeholder="Không giới hạn" value={t.kcMax ?? undefined} onChange={(v) => setTier(i, { kcMax: v === null || v === undefined ? null : Number(v) })} />,
            },
            ...[0, 1, 2].map((m) => ({
              title: `Phí T${m + 1}`, key: `fee${m}`, width: 120,
              render: (_: unknown, t: NewCreatorTier, i: number) => <InputNumber {...moneyProps} value={t.fees[m]} onChange={(v) => setTierMonth(i, 'fees', m, Number(v) || 0)} />,
            })),
            ...[0, 1, 2].map((m) => ({
              title: `% tham khảo T${m + 1}`, key: `pct${m}`, width: 100,
              render: (_: unknown, t: NewCreatorTier, i: number) => <InputNumber {...pctProps} value={rateToPct(t.pct[m])} onChange={(v) => setTierMonth(i, 'pct', m, pctToRate(Number(v)))} />,
            })),
            {
              title: '', key: 'del', width: 48,
              render: (_: unknown, __: NewCreatorTier, i: number) => (
                <Button
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={value.newCreator.tiers.length <= 1}
                  onClick={() => onChange({ ...value, newCreator: { ...value.newCreator, tiers: value.newCreator.tiers.filter((_, idx) => idx !== i) } })}
                />
              ),
            },
          ]}
        />
        <Button
          size="small"
          icon={<PlusOutlined />}
          style={{ marginTop: 8 }}
          disabled={value.newCreator.tiers.length >= 10}
          onClick={() => {
            const last = value.newCreator.tiers[value.newCreator.tiers.length - 1];
            onChange({ ...value, newCreator: { ...value.newCreator, tiers: [...value.newCreator.tiers, { ...last, label: `Bậc ${value.newCreator.tiers.length + 1}`, fees: [...last.fees] as [number, number, number], pct: [...last.pct] as [number, number, number] }] } });
          }}
        >
          Thêm bậc
        </Button>
      </Card>

      <Card size="small" title="Agency nội bộ (không tính vào tổng)" className={styles.block}>
        <div className={styles.blockHint}>Vẫn tính và xem được (bật "Hiện Agency nội bộ"), nhưng không cộng vào 4 thẻ tổng. Ghi đúng tên Nhóm của file TikTok.</div>
        <Select
          mode="tags"
          value={value.excludedGroups}
          options={groupOptions}
          onChange={(v: string[]) => onChange({ ...value, excludedGroups: v.map((x) => x.trim()).filter(Boolean) })}
          style={{ width: '100%' }}
          placeholder="Chọn hoặc gõ tên Nhóm"
        />
      </Card>

      <Card size="small" title="Email Đại diện → Agency nhận phí" className={styles.block}>
        <div className={styles.blockHint}>
          Thay sheet "STK MAIL" của web cũ: Creator có email Đại diện (cột E file TikTok) trùng dòng dưới đây thì tính phí cho Agency ghi ở dòng đó, không theo cột Nhóm.
        </div>
        <Table
          rowKey="_row"
          size="small"
          pagination={false}
          dataSource={value.emailAgency.map((r, i) => ({ ...r, _row: i }))}
          locale={{ emptyText: 'Chưa có dòng nào — mọi Creator tính theo cột Nhóm.' }}
          columns={[
            { title: 'Email Đại diện', key: 'email', render: (_, r, i) => <Input value={r.email} placeholder="daidien@gmail.com" onChange={(e) => setMap(i, { email: e.target.value })} /> },
            {
              title: 'Agency nhận phí', key: 'agency',
              render: (_, r, i) => (
                <AutoComplete
                  value={r.agency}
                  options={groupOptions}
                  filterOption={(input, option) => String(option?.value || '').toLowerCase().includes(input.toLowerCase())}
                  onChange={(v) => setMap(i, { agency: v || '' })}
                  placeholder="Tên Agency"
                  style={{ width: '100%' }}
                />
              ),
            },
            {
              title: '', key: 'del', width: 48,
              render: (_, __, i) => <Button size="small" icon={<DeleteOutlined />} onClick={() => onChange({ ...value, emailAgency: value.emailAgency.filter((_, idx) => idx !== i) })} />,
            },
          ]}
        />
        <Button size="small" icon={<PlusOutlined />} style={{ marginTop: 8 }} onClick={() => onChange({ ...value, emailAgency: [...value.emailAgency, { email: '', agency: '' }] })}>
          Thêm dòng
        </Button>
      </Card>

      <CreatorRulesEditor value={value.creatorRules || []} onChange={(creatorRules) => onChange({ ...value, creatorRules })} />

      <Card size="small" title='Creator "Đã rời đi"' className={styles.block}>
        <div className={styles.blockHint}>File TikTok trọn tháng có dòng Creator đã rời đại lý (không còn UID). Web cũ vẫn tính phí các dòng này vào Nhóm "Không thuộc nhóm".</div>
        <div className={styles.inline}>
          <Switch checked={value.departed.include} onChange={(v) => onChange({ ...value, departed: { ...value.departed, include: v } })} />
          <span>{value.departed.include ? 'Có tính, gom vào Nhóm' : 'Không tính'}</span>
          <Input
            disabled={!value.departed.include}
            value={value.departed.agencyName}
            onChange={(e) => onChange({ ...value, departed: { ...value.departed, agencyName: e.target.value } })}
            style={{ width: 240 }}
          />
        </div>
      </Card>

      <Alert
        type="info"
        showIcon
        message="Phần chính sách chưa tự tính (web cũ cũng chưa tính)"
        description={
          <ul className={styles.notComputed}>
            <li>NV E · Thợ săn tài ba: số Creator tuyển mới mỗi tháng (5 / 7 / 10 người, mỗi người 7 ngày · 15 giờ).</li>
            <li>P1 · Phạt tuyển dụng: trừ 7% / 15% / 25% theo tháng không đạt NV E.</li>
            <li>P2 · VPNT: trừ 5% hoặc 10% tổng doanh thu Agency theo số Creator vi phạm (phí từ chính Creator VPNT thì đã về 0).</li>
            <li>P3 · Đa tài khoản: trừ 5% / 10% / 30% / 50% theo số tài khoản vi phạm.</li>
            <li>Lương đảm bảo 5.000.000 đ cho Creator mới (nhập ở Thưởng Online → Khoản nhập tay).</li>
          </ul>
        }
      />
    </>
  );
};

export default PolicyEditor;
